import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AuthPage.css";

function AuthPage() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("Guest");
  const [statusMessage, setStatusMessage] = useState(
    "Checking authentication..."
  );
  const [statusColor, setStatusColor] = useState("gray");
  const [userData, setUserData] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  const performClientLogout = async (message, color) => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.warn(
        "Could not reach logout endpoint to clear server session:",
        e
      );
    }

    setIsLoggedIn(false);
    setUserData(null);
    setUsername("Guest");
    setStatusMessage(message || "Ready to log in.");
    setStatusColor(color || "gray");
    setIsLoading(false);
  };

  useEffect(() => {
    const checkAuthStatus = async () => {
      // Clear URL parameters related to auth status if they exist
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("auth") === "success" || urlParams.get("error")) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }

      try {
        const statusResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/status`,
          { credentials: "include" }
        );

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          setDebugInfo(statusData);

          if (statusData.authorized) {
            const userResponse = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/user/me`,
              { credentials: "include" }
            );

            if (userResponse.ok) {
              // Read raw text first to handle JSON parsing errors gracefully
              const rawText = await userResponse.text();

              try {
                const userData = JSON.parse(rawText);

                setIsLoggedIn(true);
                setUserData(userData);
                setUsername(userData.username);
                setStatusMessage(`Authenticated as: ${userData.username}`);
                setStatusColor("green");

                // Redirect based on IGN existence
                if (userData.igns && userData.igns.length > 0) {
                  navigate("/dashboard", { replace: true });
                } else {
                  navigate("/onboarding", { replace: true });
                }
              } catch (e) {
                await performClientLogout(
                  "Error processing user data. Please try logging in again.",
                  "red"
                );
              }
            } else {
              await performClientLogout(
                "You have been logged out from your previous session. Please log in again.",
                "red"
              );
            }
          } else {
            const wasLoggedIn =
              statusData.has_token || statusData.session_keys.length > 0;

            const defaultMessage = wasLoggedIn
              ? "Your session has expired. Please log in."
              : "Welcome! Click the button to authorize with Discord.";

            const defaultColor = wasLoggedIn ? "orange" : "gray";

            await performClientLogout(defaultMessage, defaultColor);
          }
        } else {
          const errorData = await statusResponse.json();
          setStatusMessage(
            `Status check error: ${errorData.error || "Unknown error"}`
          );
          setStatusColor("red");
        }
      } catch (error) {
        setStatusMessage(
          "Network Error: Cannot connect to Flask server. Please try again later."
        );
        setStatusColor("red");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [navigate]);

  const handleLogout = async () => {
    await performClientLogout("Logged out successfully.", "orange");
    navigate("/", { replace: true });
  };

  return (
    <div className="AuthPage">
      <header className="login-header">
        <h1 className="title">OSRS Snakes & Ladders</h1>

        {isLoading ? (
          <p className="status-message" style={{ color: "gray" }}>
            Loading, please wait... ⏳
          </p>
        ) : (
          <>
            <p className="status-message" style={{ color: statusColor }}>
              {statusMessage}
            </p>

            {isLoggedIn && userData && (
              <div className="user-info">
                <img
                  src={userData.avatar_url}
                  alt="Avatar"
                  className="user-avatar"
                  onError={(e) => {
                    e.target.src =
                      "https://cdn.discordapp.com/embed/avatars/0.png";
                  }}
                />
                <p>
                  <strong>Discord ID:</strong> {userData.discord_id}
                </p>
                {userData.igns && userData.igns.length > 0 && (
                  <p>
                    <strong>IGNs:</strong>&nbsp;
                    {userData.igns.map((ignObject) => ignObject.ign).join(", ")}
                  </p>
                )}

                <button onClick={handleLogout} className="logout-button">
                  Logout 🚪
                </button>
              </div>
            )}

            <hr className="separator" />

            {!isLoggedIn && (
              <a
                href={`${process.env.REACT_APP_API_BASE_URL}/auth/discord`}
                style={{ textDecoration: "none" }}
              >
                <button className="discord-login-button">
                  Login with Discord 🚀
                </button>
              </a>
            )}
          </>
        )}

        {/* {debugInfo && (
          <details className="debug-info">
            <summary>Debug Info (Dev Only)</summary>
            <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
          </details>
        )} */}
      </header>
    </div>
  );
}

export default AuthPage;
